import React, { ReactNode, useCallback } from 'react'
import XDate from 'xdate'
import moment from 'moment'
import { Calendar, DateData } from 'react-native-calendars'
import { View } from 'react-native'
import { MarkingProps } from 'react-native-calendars/src/calendar/day/marking'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, Text } from '@components/base'
import { useIsDarkMode } from '@src/config/theme'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import Day from './Day'

const HEADER_HEIGHT = 60
const HEADER_BUTTON_WIDTH = 60
const CALENDAR_HEADER_WIDTH = 218 // min width that fits "September 2020" with arrows

interface SchedulingCalendarProps {
  availableDates: string[]
  selectedDate: string | null
  selectedMonth: string | null
  onDayPress: (date: DateData) => void
  onMonthChange?: (date: DateData) => void
  disabled?: boolean
  variant?: 'scheduler' | 'chatbot'
}

export const SchedulingCalendar = (props: SchedulingCalendarProps) => {
  const {
    availableDates,
    selectedDate,
    selectedMonth,
    onDayPress,
    onMonthChange,
    disabled,
    variant = 'scheduler',
  } = props

  const appointmentSchedulerMonthlyDateFetchingEnabled = useFeatureFlag(
    Feature.AppointmentSchedulerMonthlyDateFetching,
  )

  const styles = useStyleSheet(themedStyles)
  const isDarkMode = useIsDarkMode()

  const todayMonth = moment().format('YYYY-MM')
  const isCurrentMonth = selectedMonth === todayMonth
  const disableLeftArrow = appointmentSchedulerMonthlyDateFetchingEnabled && isCurrentMonth

  const handleMonthChange = (date: DateData) => {
    if (disabled) {
      return
    }

    onMonthChange?.(date)
  }

  const colorConfig = styles.colors as {
    color: string
    textColor: string
  }

  const renderArrow = useCallback(
    (direction: 'left' | 'right') => {
      return <Icon name={`caret-${direction}`} style={styles.arrowIcon} />
    },
    [styles],
  )

  const renderHeader = useCallback(
    (date?: XDate): ReactNode => {
      let formattedHeader = ''

      if (appointmentSchedulerMonthlyDateFetchingEnabled && selectedMonth) {
        formattedHeader = moment(selectedMonth).format('MMMM YYYY')
      } else if (date) {
        formattedHeader = date.toString('MMMM yyyy')
      }

      return (
        <Text type="large" bold style={styles.headerTitle}>
          {formattedHeader}
        </Text>
      )
    },
    [styles.headerTitle, selectedMonth, appointmentSchedulerMonthlyDateFetchingEnabled],
  )

  const mainStyles = useStyleSheet(calendarMainStyles)
  const headerStyles = useStyleSheet(commonCalendarHeaderStyles)
  const currentDate =
    selectedDate ||
    (selectedMonth
      ? moment(selectedMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD')
      : undefined) ||
    availableDates[0]

  const markedDates = () => {
    const dates = {} as { [key: string]: MarkingProps }
    availableDates.forEach((date) => {
      dates[moment(date).format('YYYY-MM-DD')] = {
        disabled: !!disabled,
        selected: date === selectedDate,
        ...colorConfig,
      }
    })
    return dates
  }

  return (
    <View>
      <Calendar
        key={`${isDarkMode}${headerStyles.header.marginLeft}`}
        theme={{
          calendarBackground: 'transparent',
          // @ts-ignore https://github.com/wix/react-native-calendars/issues/1864
          'stylesheet.calendar.main': mainStyles,
          'stylesheet.calendar.header': headerStyles,
        }}
        style={variant === 'scheduler' ? styles.calendarContainer : styles.card}
        current={currentDate}
        onDayPress={disabled ? undefined : onDayPress}
        onMonthChange={handleMonthChange}
        renderHeader={renderHeader}
        disableArrowLeft={disabled || disableLeftArrow}
        disableArrowRight={disabled}
        renderArrow={renderArrow}
        dayComponent={Day}
        markedDates={markedDates()}
        disabledByDefault
      />
    </View>
  )
}

const calendarMainStyles = StyleService.create({
  container: {
    padding: 0,
  },
  week: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
})

const commonCalendarHeaderStyles = StyleService.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'center',
    width: CALENDAR_HEADER_WIDTH,
    height: HEADER_HEIGHT,
    marginLeft: 0,
  },
  week: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    height: 40,
  },
  dayHeader: {
    width: 32,
    textAlign: 'center',
    fontSize: 13,
    fontFamily: 'NBInternationalProReg',
    fontWeight: '400',
    color: 'theme.text.secondary',
  },
})

const themedStyles = StyleService.create({
  calendarContainer: {
    paddingBottom: 8,
    borderWidth: 2,
    borderRadius: 8,
    borderColor: 'theme.surface.base',
    marginBottom: 8,
  },
  card: {
    borderWidth: 1,
    borderColor: 'theme.surface.base1',
    borderRadius: 24,
    backgroundColor: 'theme.background',
    paddingHorizontal: 12,
    paddingBottom: 4,
  },
  headerButton: {
    position: 'absolute',
    right: 0,
    width: HEADER_BUTTON_WIDTH,
    height: HEADER_HEIGHT,
    marginRight: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderColor: 'theme.surface.base2',
  },
  headerTitle: {
    textAlign: 'center',
  },
  colors: {
    color: 'theme.primary.base',
    textColor: 'theme.solid.white',
  },
  arrowIcon: {
    height: 24,
    width: 24,
    color: 'theme.text.secondary',
  },
})
